import { api } from "./api";

export interface InstallmentResponse {
  id: string;
  emprestimoId: string;
  numero: number;
  valor: string;
  dataVencimento: string;
  dataPagamento: string | null;
  pago: boolean;
  formaPagamento: string | null;
  observacao: string | null;
  referencia: string;
}

export interface LoanResponse {
  id: string;
  tenantId: string;
  clienteId: string;
  clienteNome?: string | null;
  clienteCpf?: string | null;
  clienteBloqueado?: boolean | null;
  campanhaNome?: string | null;
  campanhaCodigo?: string | null;
  valor: string;
  valorTotal: string;
  frequencia: string;
  numParcelas: number;
  taxaJuros: string;
  origem: string;
  status: string;
  perda: boolean;
  parcelasPagas: number;
  data: string | null;
  createdAt: string;
  parcelas?: InstallmentResponse[];
  // enriquecidos pelo /loans list
  proximaParcelaNumero?: number | null;
  proximaParcelaValor?: string | null;
  proximaParcelaVencimento?: string | null;
  diasAtraso?: number | null;
  saldoDevedor?: string | null;
  temRenovacao?: boolean | null;
  perdaAt?: string | null;
  perdaBy?: string | null;
  perdaMotivo?: string | null;
  emAcordo?: boolean | null;
  acordosTotal?: number | null;
}

export interface LoansStats {
  valorTotalAtivo: string;
  contratosAtivos: number;
  diariasAReceberHoje: string;
  parcelasVencendoHoje: number;
  emDia: number;
  emAtraso: number;
  inativos: number;
  perdas: number;
  emAcordo: number;
}

export type LoanTab = "todos" | "em_dia" | "atrasado" | "vencendo_hoje" | "inativo" | "perda" | "acordo";

export interface CreateLoanData {
  clienteId: string;
  valor: number;
  frequencia: "diario" | "semanal" | "mensal";
  numParcelas: number;
  taxaJuros: number;
  dataInicioVencimento: string;
  origem?: string;
}

interface PaginatedLoans {
  data: LoanResponse[];
  meta: { page: number; limit: number; total: number; totalPages: number };
}

export async function listLoans(params: {
  page?: number;
  limit?: number;
  status?: string;
  clienteId?: string;
  search?: string;
  tab?: LoanTab;
}): Promise<PaginatedLoans> {
  const query = new URLSearchParams();
  if (params.page) query.set("page", String(params.page));
  if (params.limit) query.set("limit", String(params.limit));
  if (params.status) query.set("status", params.status);
  if (params.clienteId) query.set("clienteId", params.clienteId);
  if (params.search) query.set("search", params.search);
  if (params.tab) query.set("tab", params.tab);

  const response = await api.get<LoanResponse[]>(`/loans?${query.toString()}`);
  return {
    data: response.data || [],
    meta: response.meta || { page: 1, limit: 10, total: 0, totalPages: 0 },
  };
}

export async function getLoansStats(params: { search?: string } = {}): Promise<LoansStats> {
  const query = new URLSearchParams();
  if (params.search) query.set("search", params.search);
  const suffix = query.toString();
  const response = await api.get<LoansStats>(`/loans/stats${suffix ? `?${suffix}` : ""}`);
  return response.data!;
}

export async function getLoan(id: string): Promise<LoanResponse> {
  const response = await api.get<LoanResponse>(`/loans/${id}`);
  return response.data!;
}

export async function createLoan(data: CreateLoanData): Promise<LoanResponse> {
  const response = await api.post<LoanResponse>("/loans", data);
  return response.data!;
}

export async function deleteLoan(id: string): Promise<void> {
  await api.delete(`/loans/${id}`);
}

export async function markLoanAsLoss(id: string, motivo: string): Promise<LoanResponse> {
  const response = await api.post<LoanResponse>(`/loans/${id}/mark-loss`, { motivo });
  return response.data!;
}

export async function unmarkLoanAsLoss(id: string): Promise<LoanResponse> {
  const response = await api.post<LoanResponse>(`/loans/${id}/unmark-loss`, {});
  return response.data!;
}

export async function payInstallment(
  id: string,
  data: { formaPagamento: string; dataPagamento?: string; observacao?: string }
): Promise<InstallmentResponse> {
  const response = await api.post<InstallmentResponse>(`/installments/${id}/pay`, data);
  return response.data!;
}

export async function reverseInstallmentPayment(
  id: string,
  data: { motivo?: string } = {}
): Promise<InstallmentResponse> {
  const response = await api.post<InstallmentResponse>(`/installments/${id}/reverse`, data);
  return response.data!;
}

export async function listOverdueInstallments(): Promise<InstallmentResponse[]> {
  const response = await api.get<InstallmentResponse[]>("/installments/overdue");
  return response.data || [];
}

export async function listTodayInstallments(): Promise<InstallmentResponse[]> {
  const response = await api.get<InstallmentResponse[]>("/installments/today");
  return response.data || [];
}
